import { useId, type JSX } from 'react';
import { useT } from '../i18n/useT';

/** Inline validation message; the source strings come from `validateStep`. */
export const FieldError = ({
  message,
  id
}: {
  message: string | undefined;
  id?: string;
}): JSX.Element | null => {
  const t = useT();
  return message === undefined ? null : (
    <span id={id} className="ow-error" role="alert">
      {t(message)}
    </span>
  );
};

interface InputA11y {
  'aria-invalid': boolean;
  'aria-describedby': string | undefined;
}

/**
 * Label wrapping its input; the error sits outside the label so it is not part of the name, and
 * the input points at it with `aria-describedby`.
 */
export const Field = ({
  label,
  error,
  children
}: {
  label: string;
  error: string | undefined;
  children: (a11y: InputA11y) => JSX.Element;
}): JSX.Element => {
  const errorId = useId();
  return (
    <div className="ow-field-group">
      <label className="ow-label">
        {label}
        {children({
          'aria-invalid': error !== undefined,
          'aria-describedby': error === undefined ? undefined : errorId
        })}
      </label>
      <FieldError message={error} id={errorId} />
    </div>
  );
};
